import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Building2,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
} from "lucide-react";
import { companyAPI, clientAPI } from "../../services/api";

export default function SuperCompanies() {
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const isValidMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const [form, setForm] = useState({
    name: "",
    contact_person: "",
    email: "",
    password: "",
    mobile: "",
    address: "",
    status: "Active",
    assigned_clients: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [co, cl] = await Promise.all([companyAPI.list(), clientAPI.list()]);
      const companiesData = Array.isArray(co.data)
        ? co.data
        : co.data?.data || [];
      const clientsData = Array.isArray(cl.data)
        ? cl.data
        : cl.data?.data || [];
      setCompanies(companiesData);
      setClients(clientsData);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleClient = (id) => {
    setForm((f) => ({
      ...f,
      assigned_clients: f.assigned_clients.includes(id)
        ? f.assigned_clients.filter((x) => x !== id)
        : [...f.assigned_clients, id],
    }));
  };

  const save = async () => {
    if (!form.name) return alert("Company name required");
    if (!form.email) return alert("Email required");
    if (!isValidEmail(form.email)) return alert("Enter a valid email address");
    if (!editingId && !form.password) return alert("Password required");
    if (!form.mobile) return alert("Mobile number required");
    if (!isValidMobile(form.mobile))
      return alert("Enter a valid 10-digit mobile number");
    setSaving(true);
    try {
      if (editingId) {
        await companyAPI.update(editingId, form);
      } else {
        await companyAPI.create(form);
      }
      closeForm();
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async (c) => {
    setForm({
      name: c.name,
      contact_person: c.contact_person || "",
      email: c.email,
      password: "",
      mobile: c.mobile || "",
      address: c.address || "",
      status: c.status,
      assigned_clients: [],
    });
    setEditingId(c.id);
    setShowForm(true);
    // Fetch full record (list endpoint doesn't include assigned_clients)
    try {
      const full = await companyAPI.get(c.id);
      const fullData = full.data?.data || full.data; // ← हा fix
      setForm((f) => ({
        ...f,
        assigned_clients: fullData.assigned_clients || [],
      }));
    } catch (e) {
      /* ignore — checkboxes just stay unticked */
    }
  };

  const closeForm = () => {
    setForm({
      name: "",
      contact_person: "",
      email: "",
      password: "",
      mobile: "",
      address: "",
      status: "Active",
      assigned_clients: [],
    });
    setEditingId(null);
    setShowForm(false);
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === "Active" ? "Inactive" : "Active";
    setTogglingId(c.id);
    setCompanies((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, status: newStatus } : x)),
    );
    try {
      await companyAPI.update(c.id, { ...c, status: newStatus });
    } catch (e) {
      alert(e.message);
      setCompanies((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, status: c.status } : x)),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"? This cannot be undone.`))
      return;
    setDeletingId(c.id);
    try {
      await companyAPI.delete(c.id);
      setCompanies((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      alert(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (showForm)
    return (
      <div style={{ padding: 28, maxWidth: 1000 }}>
        <button
          onClick={closeForm}
          style={{
            background: "none",
            border: "none",
            color: "var(--text2)",
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ← Back to Companies
        </button>

        <div
          className="form-header"
          style={{
            background: "linear-gradient(135deg,#1a56db,#3b82f6)",
            borderRadius: 12,
            padding: "20px 24px",
            marginBottom: 24,
            color: "#fff",
            margin: "0 auto",
            maxWidth: 800,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Building2 size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                {editingId ? "Edit Company" : "Add New Company"}
              </h2>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.85 }}>
                Fill in all required fields to{" "}
                {editingId ? "update this" : "create a new"} company.
              </p>
            </div>
          </div>
        </div>

        <div
          className="form-container"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 28,
            margin: "0 auto",
            maxWidth: 800,
          }}
        >
          <div
            className="flex-responsive"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div className="form-group">
              <label>Company Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Technologies Pvt Ltd"
              />
            </div>
            <div className="form-group">
              <label>Contact Person Name *</label>
              <input
                value={form.contact_person}
                onChange={(e) =>
                  setForm({ ...form, contact_person: e.target.value })
                }
                placeholder="John Doe"
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="contact@company.com"
              style={{
                borderColor:
                  form.email && !isValidEmail(form.email)
                    ? "var(--danger)"
                    : undefined,
              }}
            />
            {form.email && !isValidEmail(form.email) && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--danger)",
                  marginTop: 4,
                  display: "block",
                }}
              >
                Enter a valid email address
              </span>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>
              Password {editingId ? "(leave blank to keep current)" : "*"}
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={
                  editingId
                    ? "Leave blank to keep current password"
                    : "Minimum 6 characters"
                }
                style={{ paddingRight: 40 }}
              />
              <button
                onClick={() => setShowPass(!showPass)}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text3)",
                }}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Mobile Number *</label>
            <input
              value={form.mobile}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                setForm({ ...form, mobile: val });
              }}
              placeholder="9876543210"
              maxLength={10}
              style={{
                borderColor:
                  form.mobile && !isValidMobile(form.mobile)
                    ? "var(--danger)"
                    : undefined,
              }}
            />
            {form.mobile && !isValidMobile(form.mobile) && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--danger)",
                  marginTop: 4,
                  display: "block",
                }}
              >
                Enter a valid 10-digit mobile number
              </span>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 20 }}>
            <label>Registered Address *</label>
            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Full registered address of the company"
              rows={3}
              style={{ resize: "vertical" }}
            />
          </div>

          {clients.filter((c) => c.status === "Active").length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label>Assign Clients</label>
              <div
                style={{
                  marginTop: 8,
                  padding: 10,
                  background: "var(--bg)",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                }}
              >
                {clients
                  .filter((c) => c.status === "Active")
                  .map((cl) => (
                    <div
                      key={cl.id}
                      onClick={() => toggleClient(cl.id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: "pointer",
                        fontSize: 14,
                        whiteSpace: "nowrap",
                        marginRight: 24,
                        marginBottom: 8,
                        position: "relative",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.assigned_clients.includes(cl.id)}
                        onChange={() => toggleClient(cl.id)}
                        style={{
                          width: 16,
                          height: 16,
                          margin: 0,
                          flexShrink: 0,
                          position: "static",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span style={{ position: "static" }}>{cl.name}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div
            style={{ display: "flex", gap: 10, justifyContent: "flex-start" }}
          >
            <button
              className="btn-primary"
              onClick={save}
              disabled={saving}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              ✓{" "}
              {saving
                ? editingId
                  ? "Saving..."
                  : "Creating..."
                : editingId
                  ? "Save Changes"
                  : "Create Company"}
            </button>
            <button className="btn-secondary" onClick={closeForm}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="page-container" style={{ padding: 28 }}>
      <div
        className="page-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Companies</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Manage all registered vendor companies
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingId(null);
            setForm({
              name: "",
              contact_person: "",
              email: "",
              password: "",
              mobile: "",
              address: "",
              status: "Active",
              assigned_clients: [],
            });
            setShowForm(true);
          }}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={14} /> Add Company
        </button>
      </div>

      <div style={{ position: "relative", maxWidth: 320, marginBottom: 20 }}>
        <Search
          size={13}
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--text3)",
          }}
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies..."
          style={{ paddingLeft: 30 }}
        />
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact Person</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Clients</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: "var(--accent-dim)",
                          border: "1px solid var(--accent)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Building2 size={13} color="var(--accent)" />
                      </div>
                      <span style={{ fontWeight: 500 }}>{c.name}</span>
                    </div>
                  </td>
                  <td className="text2">{c.contact_person || "—"}</td>
                  <td className="text2">{c.email}</td>
                  <td className="mono text2">{c.mobile || "—"}</td>
                  <td className="mono">{c.client_names || "—"}</td>
                  <td>
                    <span
                      className={`badge ${c.status === "Active" ? "badge-success" : "badge-info"}`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 14 }}
                    >
                      {/* Edit icon button */}
                      <button
                        onClick={() => openEdit(c)}
                        title="Edit company"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          cursor: "pointer",
                          border: "none",
                          background: "rgba(255,159,67,0.18)",
                          color: "#ff9f43",
                          flexShrink: 0,
                        }}
                      >
                        <Pencil size={14} />
                      </button>

                      {/* iOS-style toggle switch */}
                      <div
                        onClick={() => togglingId !== c.id && toggleStatus(c)}
                        title={
                          c.status === "Active"
                            ? "Click to deactivate"
                            : "Click to activate"
                        }
                        style={{
                          width: 40,
                          height: 22,
                          borderRadius: 999,
                          position: "relative",
                          cursor:
                            togglingId === c.id ? "not-allowed" : "pointer",
                          background:
                            c.status === "Active"
                              ? "var(--success)"
                              : "var(--border)",
                          transition: "background 0.2s ease",
                          opacity: togglingId === c.id ? 0.6 : 1,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 2,
                            left: c.status === "Active" ? 20 : 2,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                            transition: "left 0.2s ease",
                          }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
